import { IntlShape } from 'react-intl';
import isIP from 'validator/lib/isIP';
import isURL from 'validator/lib/isURL';
import { EucProject, EucUser } from '../types';
import { SgConnectUser } from './rights';

export const BUS_TOPIC_GLOBALLANGUAGE = 'global.language';
export const BUS_TOPIC_SGCONNECT_ACCESSTOKEN = 'sg-connect.access-token';
export const BUS_TOPIC_SGCONNECT_USERCONNECTION = 'sg-connect.user-connection';
export const BUS_TOPIC_SGCONNECT_USERINFO = 'sg-connect.user-info';
export const BUS_TOPIC_SGCONNECT_GRANTEDSCOPE = 'sg-connect.granted-scope';

type BusEventCallback<T> = (payload: T | undefined) => void;

export interface SgWidgetsBus {
  dangerouslyGetCurrentValue<T>(topicName: string): T | undefined;
  subscribe<T>(topicName: string, cb: BusEventCallback<T>): any;
  unsubscribe(handle: any): void;
}

export function getWidgetBus(): SgWidgetsBus {
  const widgetConfiguration = (window as any).SGWTWidgetConfiguration;
  if (!widgetConfiguration?.bus) {
    throw new Error('No Widgets bus found');
  }
  return widgetConfiguration.bus;
}

export function getWidget<T extends HTMLElement>(tagName: string) {
  return document.querySelector(tagName) as T;
}

export function getAuthorizationHeader(): string | undefined {
  const bus = getWidgetBus();
  if (bus) {
    return bus.dangerouslyGetCurrentValue<string>(BUS_TOPIC_SGCONNECT_ACCESSTOKEN);
  }
  return undefined;
}

export function getAccessToken(): string {
  const bus = getWidgetBus();
  if (bus) {
    const token = bus.dangerouslyGetCurrentValue<string | undefined>(BUS_TOPIC_SGCONNECT_ACCESSTOKEN);
    return token || '';
  }
  return '';
}

export const getUserInfo = (): SgConnectUser | null => {
  const bus = getWidgetBus();
  if (bus) {
    const userInfo = bus.dangerouslyGetCurrentValue<SgConnectUser>(BUS_TOPIC_SGCONNECT_USERINFO);
    return userInfo || null;
  }
  return null;
};

export const getUserInfoAsEucUser = (): EucUser | null => {
  const userInfo = getUserInfo();
  if (userInfo === null) {
    return null;
  }
  return {
    mail: userInfo.mail,
    active: true,
    igg: userInfo.igg,
    department: userInfo.rc_local_sigle,
    entity: userInfo.rc_local_sigle?.split('/')[0] || null,
    firstName: userInfo.first_name,
    lastName: userInfo.last_name,
    manager: null,
    title: null,
    userReferential: null,
    countryIso3: null,
  };
};

/* Display date with 'good' format */
export const displayDate = (date: string | null | undefined): string => {
  if (!date) {
    return 'N/A';
  }
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Intl.DateTimeFormat('en-US', options as any).format(new Date(date));
};

export const displayDateTime = (date: string | null | undefined): string => {
  if (!date) {
    return 'N/A';
  }
  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    time: 'full',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  };
  return new Intl.DateTimeFormat('en-US', options as any).format(new Date(date));
};

export const capitalize = (value?: string | null) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

/* Display mail like name and first name */
export const emailFilter = (email?: string | null, onlyName?: boolean): string => {
  if (!email) {
    return '';
  }
  const parts: string[] = email.replace('-ext', '').replace('-itec', '').split('@')[0].split('.');
  if (onlyName && parts[0]) {
    return capitalize(parts[0])!;
  }
  return parts.map((part: string, index: number) => (index === 0 ? capitalize(part) : part.toUpperCase())).join(' ');
};

// Data sensitivity const
export const DATA_SENSITIVITY = ['C1', 'C2', 'C3', 'C4'];

export function generateUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.trunc(Math.random() * 16);
    const v = c === 'x' ? r : (r & 0x3) | 0x8; // tslint:disable-line
    return v.toString(16);
  });
}

export const simpleHTML2Text = (html: string): string => html.replace(/<[^>]*>/g, '');

export const existsIntlId = (id: string | null | undefined, intl: IntlShape): boolean => {
  return id ? Boolean(intl.messages[id]) : false;
};

// return a formatted version of the id when the last char is a space for safe URL
export function getSafeParsedId(id: string) {
  return id.replace(/(\s)$/, '~');
}

// return URL encoded id and restore the space char at the end of the URL
export function getEncodedUriId(id: string) {
  return encodeURIComponent(id.replace(/~$/, ' '));
}

export function isNullOrEmpty(s: string | null | undefined): boolean {
  return s === null || s === undefined || s.trim() === '';
}

export function convertLabelToCamelCaseLabelId(label: string | null | undefined): string | null {
  if (isNullOrEmpty(label)) {
    return null;
  }
  if (label?.length === 1) {
    return label.toLowerCase();
  }

  const tmp = label?.replace(' ', '') ?? '';
  return tmp[0].toLowerCase() + tmp.substring(1);
}

export function convertLabelToKebabCaseLabelId(label: string | null | undefined): string | null {
  if (isNullOrEmpty(label)) {
    return null;
  }

  return label?.toLowerCase().replace(' ', '-') ?? '';
}

export const isActiveGROUPv2 = (window as any).globalConfiguration?.isActiveGROUPv2 === true;

export const sanitizeString = (value: string | null | undefined): string | null | undefined =>
  typeof value === 'string' ? value.trim() : value;

const isValidHttpUrl = (value: string): boolean => {
  const url = isURL(value, {
    protocols: ['http', 'https'],
    require_protocol: true,
    require_tld: false,
  })
    ? new URL(value)
    : null;

  return (
    url !== null &&
    !isIP(url.hostname.replace(/(^\[|\]$)/g, '')) &&
    Array.from(value).every((character) => character.trim() !== '')
  );
};

const isValidUncPath = (value: string): boolean => {
  if (!value.startsWith('\\\\') || value.includes('\r') || value.includes('\n')) {
    return false;
  }

  const pathParts = value.slice(2).split('\\');
  const server = pathParts[0];
  const share = pathParts[1];
  return !!server && !!share && !server.includes(' ') && !isIP(server);
};

const isValidDrivePath = (value: string): boolean => {
  if (value.startsWith('\\\\') || value.includes('\r') || value.includes('\n')) {
    return false;
  }

  const drive = value.slice(0, 2);
  return (
    drive.length === 2 && drive[1] === ':' && drive[0].toLowerCase() !== 'c' && value[2] === '\\' && value.length > 3
  );
};

export function isValidLocationOrRepository(
  values: Pick<EucProject, 'location' | 'versionControl'>,
  isReadOnly: (field: keyof Pick<EucProject, 'location' | 'versionControl'>) => boolean,
  field: keyof Pick<EucProject, 'location' | 'versionControl'>,
  errors: Record<string, string>
): void {
  const value = values[field];
  if (isNullOrEmpty(value) || isReadOnly(field)) {
    return;
  }

  const sanitizedValue = sanitizeString(value);
  const isValid =
    !!sanitizedValue &&
    (isValidHttpUrl(sanitizedValue) || isValidDrivePath(sanitizedValue) || isValidUncPath(sanitizedValue));
  if (!isValid) {
    errors[field] = field;
  }
}

export const STANDARD_REF_ERROR_MESSAGE = 'Only EUC with latest standard can be certified.';
